/**
 * Le chiamate alle API, una funzione per endpoint.
 *
 * Sono tipizzate con i DTO di `shared`, gli stessi che il server usa per
 * rispondere: un campo che cambia forma da una parte non compila dall'altra.
 */
import type {
  AlreadyReturnedDto,
  ChipDto,
  CreateChipInput,
  CreateEventInput,
  CreateUserInput,
  EventDto,
  EventOperationsDto,
  EventStateDto,
  EventSummaryDto,
  EventVersionDto,
  ExistingChipDto,
  ImportChipsInput,
  ImportResultDto,
  LoginInput,
  ResetPasswordInput,
  ReturnLogDto,
  Totals,
  UpdateEventInput,
  UpdateUserInput,
  UserDto,
} from '@pietrochip/shared';
import { apiDownload, apiFetch } from './client';

// ── Autenticazione ───────────────────────────────────────────────────────────

export function login(input: LoginInput): Promise<{ user: UserDto }> {
  return apiFetch('/api/auth/login', { method: 'POST', body: input });
}

export function logout(): Promise<void> {
  return apiFetch('/api/auth/logout', { method: 'POST' });
}

export function fetchMe(): Promise<{ user: UserDto; freeAccess: boolean }> {
  return apiFetch('/api/auth/me');
}

export function changeOwnPassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  return apiFetch('/api/auth/password', { method: 'POST', body: input });
}

// ── Eventi ───────────────────────────────────────────────────────────────────

export function listEvents(): Promise<{ events: EventSummaryDto[] }> {
  return apiFetch('/api/events');
}

export function createEvent(input: CreateEventInput): Promise<{ event: EventDto }> {
  return apiFetch('/api/events', { method: 'POST', body: input });
}

export function getEventState(eventId: string): Promise<EventStateDto> {
  return apiFetch(`/api/events/${eventId}`);
}

/**
 * L'impronta dello stato dell'evento, per il ricaricamento di riserva.
 *
 * Costa poche decine di byte: è ciò che permette di ricontrollare ogni due
 * secondi senza scaricare ogni volta tutti i chip dell'evento.
 */
export function getEventVersion(eventId: string): Promise<EventVersionDto> {
  return apiFetch(`/api/events/${eventId}/version`);
}

export function updateEvent(
  eventId: string,
  input: UpdateEventInput,
): Promise<{ event: EventDto }> {
  return apiFetch(`/api/events/${eventId}`, { method: 'PATCH', body: input });
}

export function deleteEvent(eventId: string): Promise<void> {
  return apiFetch(`/api/events/${eventId}`, { method: 'DELETE' });
}

/**
 * Archivia un evento: esce dalla home, ma resta intero e recuperabile.
 * Riservata agli amministratori, come creazione ed eliminazione.
 */
export function archiveEvent(eventId: string): Promise<{ event: EventDto }> {
  return apiFetch(`/api/events/${eventId}/archive`, { method: 'POST' });
}

/** Riporta nell'elenco attivo un evento archiviato. */
export function unarchiveEvent(eventId: string): Promise<{ event: EventDto }> {
  return apiFetch(`/api/events/${eventId}/unarchive`, { method: 'POST' });
}

// ── Chip ─────────────────────────────────────────────────────────────────────

export function importChips(
  eventId: string,
  input: ImportChipsInput,
): Promise<ImportResultDto> {
  return apiFetch(`/api/events/${eventId}/chips/import`, { method: 'POST', body: input });
}

export function createChip(
  eventId: string,
  input: CreateChipInput,
): Promise<{ chip: ChipDto; log: ReturnLogDto | null }> {
  return apiFetch(`/api/events/${eventId}/chips`, { method: 'POST', body: input });
}

export function returnChip(
  eventId: string,
  chipId: string,
): Promise<{ chip: ChipDto; log: ReturnLogDto }> {
  return apiFetch(`/api/events/${eventId}/chips/${chipId}/return`, { method: 'POST' });
}

export function unreturnChip(
  eventId: string,
  chipId: string,
): Promise<{ chip: ChipDto; log: ReturnLogDto | null }> {
  return apiFetch(`/api/events/${eventId}/chips/${chipId}/unreturn`, { method: 'POST' });
}

/** I chip già in elenco, con l'anagrafica, per l'anteprima di import. */
export function fetchEventCodes(
  eventId: string,
): Promise<{ chips: ExistingChipDto[]; codes: string[]; totals: Totals }> {
  return apiFetch(`/api/events/${eventId}/codes`);
}

/** Chip archiviati dell'evento, letti quando si apre la sezione «Rimossi». */
export function fetchRemovedChips(eventId: string): Promise<{ chips: ChipDto[] }> {
  return apiFetch(`/api/events/${eventId}/chips/removed`);
}

/**
 * Il registro completo dell'evento.
 *
 * Separato da `getEventState`, che porta solo le ultime 25 operazioni: questa è
 * la lettura che si fa aprendo la pagina del registro, e a ogni riconnessione.
 */
export function fetchEventOperations(eventId: string): Promise<EventOperationsDto> {
  return apiFetch(`/api/events/${eventId}/operations`);
}

export function removeChip(
  eventId: string,
  chipId: string,
): Promise<{ chip: ChipDto; log: ReturnLogDto }> {
  return apiFetch(`/api/events/${eventId}/chips/${chipId}/remove`, { method: 'POST' });
}

export function restoreChip(
  eventId: string,
  chipId: string,
): Promise<{ chip: ChipDto; log: ReturnLogDto | null }> {
  return apiFetch(`/api/events/${eventId}/chips/${chipId}/restore`, { method: 'POST' });
}

// ── Export ───────────────────────────────────────────────────────────────────

export function downloadExport(eventId: string) {
  return apiDownload(`/api/events/${eventId}/export.xlsx`);
}

// ── Utenti ───────────────────────────────────────────────────────────────────

export function listUsers(): Promise<{ users: UserDto[] }> {
  return apiFetch('/api/users');
}

export function createUser(input: CreateUserInput): Promise<{ user: UserDto }> {
  return apiFetch('/api/users', { method: 'POST', body: input });
}

export function updateUser(
  userId: string,
  input: UpdateUserInput,
): Promise<{ user: UserDto }> {
  return apiFetch(`/api/users/${userId}`, { method: 'PATCH', body: input });
}

export function resetUserPassword(
  userId: string,
  input: ResetPasswordInput,
): Promise<void> {
  return apiFetch(`/api/users/${userId}/password`, { method: 'POST', body: input });
}

export type { AlreadyReturnedDto };
